import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import type { MatchScreenRedesignConfig } from '../types'
import '../types'

const DEFAULT_CONFIG: MatchScreenRedesignConfig = Object.freeze({
  enabled: false,
  variationKey: '',
  variables: Object.freeze({}) as Record<string, unknown>
})

const RedesignConfigContext = createContext<MatchScreenRedesignConfig | undefined>(undefined)

/**
 * On admin previews only, `?variant=` forces which match screen
 * renders so compliance can review both, regardless of the server-side
 * redesign decision. `window.matchScreenRedesign` is deep-frozen by SAapp, so
 * the override is applied here at read time, never by mutating the global.
 * Forcing `redesign` when the server decision was off renders with default
 * module config (empty `variables`).
 */
const applyPreviewVariantOverride = (
  config: MatchScreenRedesignConfig
): MatchScreenRedesignConfig => {
  const variant = new URLSearchParams(window.location.search).get('variant')
  if (variant !== 'legacy' && variant !== 'redesign') return config

  return {
    ...config,
    enabled: variant === 'redesign',
    variationKey: `preview-forced-${variant}`
  }
}

/**
 * Reads `window.matchScreenRedesign` once and exposes the normalized config
 * (with `{ enabled: false, variationKey: '', variables: {} }` fallback) via
 * Context. Mount inside `MatchScreenProvider` so both branches can read it.
 *
 * Intentionally does NOT call `useDecision()` — the flag is server-evaluated.
 *
 * @example
 * <RedesignConfigProvider>
 *   <MatchScreenApp />
 * </RedesignConfigProvider>
 */
export function RedesignConfigProvider ({ children, isPreview = false }: { children: ReactNode, isPreview?: boolean }) {
  const config = useMemo<MatchScreenRedesignConfig>(() => {
    const raw = typeof window !== 'undefined' ? window.matchScreenRedesign : undefined
    const normalized = (!raw || typeof raw !== 'object')
      ? DEFAULT_CONFIG
      : {
          enabled: raw.enabled === true,
          variationKey: typeof raw.variationKey === 'string' ? raw.variationKey : '',
          variables: raw.variables && typeof raw.variables === 'object' ? raw.variables : {}
        }

    return isPreview ? applyPreviewVariantOverride(normalized) : normalized
  }, [isPreview])

  return (
    <RedesignConfigContext.Provider value={config}>
      {children}
    </RedesignConfigContext.Provider>
  )
}

/**
 * Returns the normalized redesign config. For module-specific config, prefer
 * `useModuleConfig()`. Must be called inside `RedesignConfigProvider`.
 *
 * @example
 * const { enabled } = useMatchScreenRedesign()
 * return enabled ? <MatchScreenAppRedesigned /> : <LegacyMatchScreen />
 */
export function useMatchScreenRedesign (): MatchScreenRedesignConfig {
  const context = useContext(RedesignConfigContext)
  if (!context) {
    throw new Error('useMatchScreenRedesign must be used within a RedesignConfigProvider')
  }
  return context
}
